import { FC } from 'react';
import { ExclusionOrderModalProps } from './exclusionOrderModal.type';
import { ExclusionOrderModel } from '../../models/exclusionOrderModel';
import ModalComponent from '../modal/modal';
import { useTranslation } from 'react-i18next';
import styles from './exclusionOrderModal.module.css';
import {
  FieldItem,
  getGeneralInfoFields,
  getPoiDetailsFields,
  getExclusionPeriodFields,
} from './exclusionOrderModal.helper';

const FieldGrid: FC<{ fields: FieldItem[] }> = (props) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
    {props.fields.map((field: FieldItem) => (
      <div key={field.label}>
        <span className={styles.fieldLabel}>{field.label}</span>
        <p>{field.value}</p>
      </div>
    ))}
  </div>
);

const Section: FC<{ label: string; children: React.ReactNode }> = (props) => (
  <section className={styles.section}>
    <div className={styles.sectionLabel}>{props.label}</div>
    <div className={styles.sectionContent}>{props.children}</div>
  </section>
);

const ExclusionOrderModal: FC<ExclusionOrderModalProps> = (props) => {
  const { t } = useTranslation();

  if (!props.exclusionOrder) {
    return null;
  }

  const order: ExclusionOrderModel = props.exclusionOrder;

  const generalInfoFields: FieldItem[] = getGeneralInfoFields(t, order);
  const poiDetailsFields: FieldItem[] = getPoiDetailsFields(t, order);
  const exclusionPeriodFields: FieldItem[] = getExclusionPeriodFields(t, order);

  return (
    <>
      {props.isOpen && <div className={styles.exclusionModalBackdrop} />}
      <ModalComponent
        open={props.isOpen}
        onToggle={(open) => {
          if (!open) props.onClose();
        }}
        title={`${t('exclusionOrder.title')} — #${order.reportNo}`}
        onClose={props.onClose}
        isShowClose
        showAction
        footerHidden
        centered
        dialogClassName="w-[90vw] sm:w-[80vw] md:w-[36rem] lg:w-[50rem]"
      >
        <div className="p-4 overflow-y-auto max-h-[70vh]">
          <Section label={t('exclusionOrder.generalInfo')}>
            <FieldGrid fields={generalInfoFields} />
          </Section>

          {(props.faceImage || props.fullImage) && (
            <Section label={t('exclusionOrder.poiPhotos')}>
              <div className="flex gap-3">
                {props.faceImage && (
                  <img
                    src={props.faceImage}
                    alt="POI face"
                    className="aspect-[3/4] w-[11rem] object-cover rounded"
                  />
                )}
                {props.fullImage && (
                  <img
                    src={props.fullImage}
                    alt="POI full"
                    className="aspect-[3/4] w-[11rem] object-cover rounded"
                  />
                )}
              </div>
            </Section>
          )}

          <Section label={t('exclusionOrder.poiDetails')}>
            <FieldGrid fields={poiDetailsFields} />
          </Section>

          <Section label={t('exclusionOrder.exclusionPeriod')}>
            <FieldGrid fields={exclusionPeriodFields} />
          </Section>

          {order.reason && (
            <Section label={t('exclusionOrder.notes')}>
              <p className="text-sm">{order.reason}</p>
            </Section>
          )}

          {order.reentryConditions && (
            <Section label={t('exclusionOrder.reentryConditions')}>
              <p className="text-sm">{order.reentryConditions}</p>
            </Section>
          )}

          <Section label={t('exclusionOrder.issuedBy')}>
            {order.issuedBySignatureUrl && (
              <img
                src={order.issuedBySignatureUrl}
                alt="Issued by signature"
                className="max-h-16 mb-2"
              />
            )}
            <p className="text-sm">{order.issuedByFullName}</p>
            <p className="text-sm">{order.createdAtFormatted}</p>
          </Section>

          {order.gamblerConsent && (
            <Section label={t('exclusionOrder.gambler')}>
              <p className="text-sm">{t(order.gamblerConsentLabel)}</p>
              {order.gamblerSignatureUrl && (
                <img
                  src={order.gamblerSignatureUrl}
                  alt="Gambler signature"
                  className="max-h-16 mt-2 mb-2"
                />
              )}
              {order.gamblerSignedAtFormatted && (
                <>
                  <p className="text-sm">{order.playerFullName}</p>
                  <p className="text-sm">{order.gamblerSignedAtFormatted}</p>
                </>
              )}
            </Section>
          )}
        </div>
      </ModalComponent>
    </>
  );
};

export default ExclusionOrderModal;
